/**
 * INTERRUPTEUR `ENEDIS_PAGE_DONNEES_V2` (DEV-1508, lot 3).
 * ============================================================================
 *
 * INTENTION : engagement E11 du contrat de la page. La nouvelle vue « Données du
 * compteur » d'un compteur Enedis vit derrière un interrupteur. Désarmé, un
 * utilisateur voit STRICTEMENT la page d'avant : pas un composant de plus, pas un
 * appel de plus. Armé, il voit la vue v2.
 *
 * CE QUI A CHANGÉ AU LOT 3 : l'interrupteur n'est plus une constante de build.
 * Il vaut ce que le dyno dit, et il dit une POPULATION — personne, tout le monde,
 * ou une liste de jetons. On ouvre donc la page à qui doit la voir la première,
 * on regarde, puis on referme, sans reconstruire ni redéployer quoi que ce soit.
 * La valeur arrive par `GET /api/interrupteurs`, lue une fois après
 * l'authentification (`stores/interrupteurs.store.ts`).
 *
 * CE QUI A CHANGÉ AU LOT 3d : un jeton peut désigner des PERSONNES, plus
 * seulement une société. Une société de travail porte des centaines de comptes
 * extérieurs à l'équipe : l'armer par identifiant montrerait la page à des
 * inconnus. Trois formes de jeton, mélangeables dans la même liste :
 *
 *   • `<id>`       → la société qui porte cet identifiant (comparaison exacte) ;
 *   • `@<domaine>` → la session dont le courriel de connexion finit par ce
 *                    suffixe, arobase comprise, sans égard à la casse ;
 *   • `staff`      → la session portant le drapeau interne d'équipe. Depuis
 *                    DEV-2306, ce drapeau est la mention `staff` que le
 *                    serveur signe dans le jeton de session à une connexion
 *                    au mot de passe maître, jamais le stockage du navigateur.
 *
 * CE QUI CHANGE AU LOT 3e : un jeton qui porte une arobase SANS commencer par
 * elle désigne UNE PERSONNE, et une seule — son courriel de connexion, comparé
 * en entier, sans égard à la casse, après avoir ôté les espaces de bord :
 *
 *   • `<courriel>` → la session ouverte sous ce courriel exact.
 *
 * POURQUOI : la prod ne doit montrer la page qu'au compte qui la pilote. Les
 * autres comptes de l'équipe, et les sessions ouvertes au mot de passe maître,
 * servent à faire des démonstrations chez des clients : ils doivent continuer
 * de voir la page d'avant. Ni `@<domaine>` ni `staff` ne savent dire « celui-là
 * et pas les autres » ; ce jeton-ci le sait.
 *
 * La comparaison est ENTIÈRE, jamais par préfixe ni par suffixe : un courriel
 * qui prolonge le jeton d'un caractère à gauche ou d'un domaine à droite est un
 * AUTRE compte, et il n'est pas armé.
 *
 * CE QUI CHANGE AU LOT DE SÉCURITÉ DES INTERRUPTEURS : la route est publique, et
 * un courriel posé sur le dyno n'y est plus jamais servi en clair. Le serveur
 * remplace chaque jeton courriel par `sha256:<hex>`, l'empreinte SHA-256 du
 * courriel en minuscules, tous espaces retirés (`backend/src/config/
 * interrupteurs.ts`). Le navigateur calcule la même empreinte sur le courriel
 * de la session (Web Crypto, donc de façon ASYNCHRONE : le store la calcule
 * pendant `charger()`, avant que la moindre garde ne tranche) et compare :
 *
 *   • `sha256:<hex>` → la session dont le courriel a cette empreinte. Préfixe et
 *                      hexadécimal lus sans égard à la casse.
 *
 * Empreinte inconnue (Web Crypto absent ou en échec, calcul pas encore fait,
 * ou fait pour une AUTRE session) : le jeton n'arme personne.
 *
 * Aucun domaine ni identifiant n'est écrit ici : la population vit sur le dyno,
 * jamais dans le bundle. C'est ce qui permet de servir la même application sous
 * une autre marque sans y lire le nom de personne.
 *
 * ⚠️ Le défaut est DÉSARMÉ, et tout ce qu'on ne sait pas lire l'est aussi : route
 * injoignable, réponse illisible, société inconnue, jeton inconnu, courriel ou
 * drapeau absents, valeur d'un type inattendu. Un interrupteur qui s'arme sur un
 * accident est un déploiement que personne n'a décidé.
 */

/** Ce que la route peut dire d'un interrupteur. */
export type ValeurInterrupteur = "off" | "all" | string[]

/** Nom de la variable d'environnement du dyno, cité par `docs/DEPLOY.md`. */
export const CLE_ENEDIS_PAGE_DONNEES_V2 = "ENEDIS_PAGE_DONNEES_V2"

/** Chemin de lecture, servi par le backend Express du même dyno. */
export const ROUTE_INTERRUPTEURS = "/api/interrupteurs"

/** Le jeton qui désigne une session d'équipe, en minuscules. */
export const JETON_STAFF = "staff"

/**
 * Qui regarde : ce qu'il faut savoir de la session pour trancher un jeton.
 *
 * Chaque champ est facultatif, et son absence FERME : une session dont on ne
 * connaît pas le courriel n'est armée ni par un suffixe de domaine ni par un
 * jeton de courriel, et une session dont on ne connaît pas le drapeau d'équipe
 * n'est jamais « staff ».
 */
export interface ContexteDeSession {
    /** La société de l'utilisateur connecté. */
    companyId?: string | null
    /** Le nom d'utilisateur, c'est-à-dire son courriel de connexion. */
    username?: string | null
    /** La mention d'équipe signée dans le jeton de session (DEV-2306). */
    staff?: boolean | null
    /**
     * L'empreinte `sha256:<hex>` du courriel de connexion, calculée par
     * `empreinteDuCourriel` pour CETTE session. Absente, elle ferme tout jeton
     * `sha256:`.
     */
    empreinteCourriel?: string | null
}

/** Le préfixe d'un jeton qui porte l'empreinte d'un courriel. */
export const PREFIXE_EMPREINTE = "sha256:"

/**
 * Un courriel ramené à la forme qu'on compare et qu'on hache : minuscules, tous
 * espaces retirés. `null` quand il n'y a rien à comparer. Doit rester identique
 * à la normalisation du serveur (`backend/src/config/interrupteurs.ts`).
 */
export function normaliserCourriel(brut: unknown): string | null {
    if (typeof brut !== "string") return null
    const courriel = brut.replace(/\s+/g, "").toLowerCase()
    return courriel === "" ? null : courriel
}

/**
 * L'empreinte `sha256:<hex>` d'un courriel, calculée par Web Crypto.
 *
 * Ne rejette jamais : courriel absent, contexte non sécurisé (pas de
 * `crypto.subtle`) ou calcul en échec rendent `null`, et un `null` n'arme
 * aucun jeton `sha256:`.
 */
export async function empreinteDuCourriel(
    brut: unknown
): Promise<string | null> {
    const courriel = normaliserCourriel(brut)
    if (courriel === null) return null
    try {
        const subtil = globalThis.crypto?.subtle
        if (!subtil) return null
        const condense = await subtil.digest(
            "SHA-256",
            new TextEncoder().encode(courriel)
        )
        const hex = Array.from(new Uint8Array(condense))
            .map((octet) => octet.toString(16).padStart(2, "0"))
            .join("")
        return PREFIXE_EMPREINTE + hex
    } catch {
        return null
    }
}

/** Vrai quand la liste porte au moins un jeton d'empreinte. */
export function porteUneEmpreinte(
    valeur: ValeurInterrupteur | null | undefined
): boolean {
    return (
        Array.isArray(valeur) &&
        valeur.some((jeton) =>
            jeton.trim().toLowerCase().startsWith(PREFIXE_EMPREINTE)
        )
    )
}

/**
 * Vrai quand un jeton de la liste désigne la session qui regarde.
 *
 * Un jeton vide ou inconnu n'arme personne : il tombe dans la comparaison
 * exacte avec l'identifiant de société, qu'il ne peut satisfaire.
 */
/**
 * Le courriel de connexion de la session, prêt à être comparé, ou `null` quand
 * on ne le connaît pas.
 *
 * L'absence FERME : une session sans courriel n'est armée par aucun jeton qui
 * en désigne un. Les espaces de bord sont ôtés et la casse ramenée au bas, de
 * sorte que deux écritures du même compte soient le même compte — et que rien
 * d'autre ne le soit.
 */
function courrielDeLaSession(contexte: ContexteDeSession): string | null {
    return normaliserCourriel(contexte.username)
}

function jetonDesigne(jeton: string, contexte: ContexteDeSession): boolean {
    if (jeton === "") return false

    if (jeton.toLowerCase() === JETON_STAFF) return contexte.staff === true

    // L'empreinte d'un courriel : la seule forme sous laquelle le serveur sert
    // une personne. Comparée entière, sans égard à la casse de l'hexadécimal.
    const attendue = jeton.trim().toLowerCase()
    if (attendue.startsWith(PREFIXE_EMPREINTE)) {
        const empreinte = contexte.empreinteCourriel
        if (typeof empreinte !== "string") return false
        // Un préfixe seul n'est l'empreinte de personne.
        if (attendue.length <= PREFIXE_EMPREINTE.length) return false
        return empreinte.toLowerCase() === attendue
    }

    if (jeton.startsWith("@")) {
        // Un « @ » seul ne désigne pas un domaine : il armerait tout courriel.
        if (jeton.length < 2) return false
        const courriel = courrielDeLaSession(contexte)
        if (courriel === null) return false
        // Le suffixe comprend l'arobase, et il doit finir le courriel : un
        // domaine qui en préfixe un autre, ou qui se voit prolongé d'un
        // niveau de plus, ne désigne pas la même population.
        return courriel.endsWith(jeton.trim().toLowerCase())
    }

    // Une arobase ailleurs qu'en tête : le jeton est un COURRIEL, et il désigne
    // une seule personne (lot 3e). La comparaison porte sur le courriel entier,
    // jamais sur un de ses bouts.
    if (jeton.includes("@")) {
        const courriel = courrielDeLaSession(contexte)
        if (courriel === null) return false
        return courriel === normaliserCourriel(jeton)
    }

    const companyId = contexte.companyId
    if (typeof companyId !== "string" || companyId === "") return false
    return jeton === companyId
}

/**
 * Vrai quand la session qui regarde fait partie de la population armée.
 *
 * @param valeur - ce que la route a dit, ou `null` tant qu'elle n'a rien dit
 * @param contexte - ce qu'on sait de la session (société, courriel, drapeau)
 */
export function estArme(
    valeur: ValeurInterrupteur | null | undefined,
    contexte: ContexteDeSession = {}
): boolean {
    if (valeur === "all") return true
    if (!Array.isArray(valeur)) return false
    return valeur.some((jeton) => jetonDesigne(jeton, contexte))
}

/**
 * Relit ce que la route a répondu. Tout ce qui n'est pas une forme attendue
 * vaut « personne » : la réponse vient du réseau, elle n'est pas un contrat.
 */
export function lireValeurServie(brut: unknown): ValeurInterrupteur {
    if (brut === "all") return "all"
    if (Array.isArray(brut)) {
        const jetons = brut.filter(
            (s): s is string => typeof s === "string" && s.trim() !== ""
        )
        return jetons.length > 0 ? jetons : "off"
    }
    return "off"
}

/**
 * PAR OÙ ON EST ENTRÉ dans la page « Données du compteur ».
 *
 * `shadow` : l'adresse dédiée `/source/:_id_source/donnees-compteur`, que rien
 * dans l'application ne donne. Tant qu'elle est la seule entrée, une visite est
 * une visite d'équipe, pas d'un client : les métriques d'adoption ne doivent pas
 * la compter comme un usage.
 *
 * `standard` : la page de données habituelle, une fois l'interrupteur armé. Là,
 * une visite est un vrai usage.
 *
 * L'événement `Compteur Page Opened` porte cette valeur : c'est elle qui
 * mesurera la bascule.
 */
export type EntreePageCompteur = "shadow" | "standard"
